import {
  type CSSProperties,
  type FC,
  type ReactNode,
  useState,
  useEffect,
  useMemo,
} from "react";
import type { Dayjs } from "dayjs";
import { Select, Input, Form } from "antd";
import type { TablePaginationConfig, TableProps } from "antd";
import type {
  ColumnsType,
  FilterValue,
  SorterResult,
} from "antd/es/table/interface";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { AppStrings } from "../../../utils/constants/AppStrings";
import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import { KPI_CARD_BACKGROUND } from "../../../utils/Constants";
import { getColorBasedOnValue } from "../../../utils/colorUtils";
import "./stylesSuspensionsManualActions.css";
import DataTable from "../../shared/dashboards/DataTable/DataTable";
import AgentPerformanceView from "../AgentPerformanceView/AgentPerformanceView";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { getManualActionsStaffManagementHref } from "../manualActionsStaffDeepLink";
import { convertToUserTimezone } from "../../../utils/dateService";
import CustomAutoCompleteControlled from "../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled";
import {
  useGetAALSuspensionsTable,
  type SuspensionsAALTableFilters,
  type SuspensionsAALTableItem,
} from "../../../queries/agent-audit-log/suspensions/useGetAALSuspensionsTable";
import { useGetAALSuspensionsKPIs } from "../../../queries/agent-audit-log/suspensions/useGetAALSuspensionsKPIs";
import { toAalSort } from "../manualActionsTableShared";
import { useDownloadAALSuspensionsTable } from "../../../queries/agent-audit-log/suspensions/useDownloadAALSuspensionsTable";
import { useDownloadOnTrigger } from "../../../hooks/useDownloadOnTrigger";
import { useGetRoles } from "../../../queries/useRolesApi";
import { useGetStaff } from "../../../queries/useGetStaff";

// CustomAutoCompleteControlled is a JS component — cast to avoid incorrect prop inference
type AgentAutoCompleteOption = { value: string; label: string; id: string };
type AgentAutoCompleteProps = {
  allOptions: AgentAutoCompleteOption[];
  selector: string;
  value: string;
  handleChange: (val: string) => void;
  clearOptions: () => void;
  placeholder?: string;
};
const TypedAgentAutoComplete =
  CustomAutoCompleteControlled as unknown as FC<AgentAutoCompleteProps>;

dayjs.extend(utc);

export type SuspensionsManualActionsProps = {
  agentPerformanceView?: boolean;
  showFilters?: boolean;
  dates?: [Dayjs, Dayjs] | null;
  downloadTrigger?: number;
};

type PanelFilters = {
  agent: string;
  role: string | null;
  username: string;
  action_type: string | null;
};

/** API may return extra fields in agent-performance mode. */
type SuspensionsRow = SuspensionsAALTableItem & {
  users_suspended?: number | string;
  users_unsuspended?: number | string;
  users_suspended_delta?: number | string;
  users_unsuspended_delta?: number | string;
  users_suspended_percent?: number;
  users_unsuspended_percent?: number;
  users_suspended_pct_change?: number;
  users_unsuspended_pct_change?: number;
};

type ApvDataRow = SuspensionsRow & { key: string };

// DataTable is a JS component — cast to a typed FC to avoid incorrect prop inference
type SuspensionsDataTableProps = {
  columns: ColumnsType<SuspensionsRow>;
  dataSource: SuspensionsRow[];
  loading: boolean;
  onChange: TableProps<SuspensionsRow>["onChange"];
  rowKey: (record: SuspensionsRow) => string;
  scroll: { x: number; y: number };
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, size: number) => void;
  };
};
const TypedSuspensionsDataTable =
  DataTable as unknown as FC<SuspensionsDataTableProps>;

const INITIAL_PANEL_FILTERS: PanelFilters = {
  agent: "",
  role: null,
  username: "",
  action_type: null,
};

const decisionOptions = [
  { value: "USER_SUSPENDED", label: "Suspend" },
  { value: "USER_UNSUSPENDED", label: "Unsuspend" },
];

/**
 * Maps Ant Design column sort `field` to backend sort field.
 * - Detail table: `created_at` → `date`
 * - Agent performance: `users_suspended` / `users_unsuspended` → `*_delta`
 */
function suspensionsSortFieldForApi(field: string): string {
  if (field === "created_at") return "date";
  if (field === "users_suspended") return "users_suspended_delta";
  if (field === "users_unsuspended") return "users_unsuspended_delta";
  return field;
}

function sortFieldFromSorter(
  field: SorterResult<SuspensionsRow>["field"],
): string | null {
  if (field === undefined || field === null) return null;
  if (Array.isArray(field)) {
    const first = field[0];
    return first !== undefined && first !== null ? String(first) : null;
  }
  return String(field);
}

function suspensionsScopeFilters(params: {
  agentPerformanceView: boolean;
  selectedRole: string[];
  appliedFilters: PanelFilters;
}): Record<string, string> {
  const { agentPerformanceView, selectedRole, appliedFilters } = params;
  if (agentPerformanceView) {
    if (selectedRole.length === 0) return {};
    return { agent_role_id: selectedRole[0] };
  }
  const out: Record<string, string> = {};
  if (appliedFilters.agent) out.agent_id = appliedFilters.agent;
  if (appliedFilters.role) out.agent_role_id = appliedFilters.role;
  if (appliedFilters.username) out.username = appliedFilters.username;
  if (appliedFilters.action_type) out.action_type = appliedFilters.action_type;
  return out;
}

const decisionColor = (actionType: string | undefined): string => {
  if (actionType === "USER_SUSPENDED") return "#E74C3C";
  if (actionType === "USER_UNSUSPENDED") return "#71BF8A";
  return "#6b7280";
};

function SuspensionsExternalLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="suspensions-manual-actions-hoverable"
    >
      {children}
    </a>
  );
}

function ApvDeltaPercent({ percent }: { percent: number | null | undefined }) {
  const sign = percent != null && percent >= 0 ? "+" : "";
  return (
    <span
      style={{
        marginLeft: "4px",
        color: getColorBasedOnValue(percent ?? 0),
        fontSize: "11px",
      }}
    >
      {sign}
      {percent != null ? Number(percent).toFixed(1) : "0.0"}%
    </span>
  );
}

function AgentPerformanceMetricCell({
  count,
  percent,
}: {
  count: number;
  percent: number | null | undefined;
}) {
  return (
    <span>
      {count}
      {percent != null && <ApvDeltaPercent percent={percent} />}
    </span>
  );
}

function RoleNameWithDot({
  role,
  fallbackLabel,
}: {
  role: { name: string; color: string } | undefined;
  fallbackLabel: string;
}) {
  if (!role) return <span>{fallbackLabel || "—"}</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: role.color,
          flexShrink: 0,
          display: "inline-block",
        }}
      />
      <span>{role.name}</span>
    </div>
  );
}

function flexRowDisplay(visible: boolean): CSSProperties {
  return { display: visible ? "flex" : "none" };
}

function SuspensionsManualActions({
  agentPerformanceView = false,
  showFilters = false,
  dates,
  downloadTrigger = 0,
}: SuspensionsManualActionsProps) {
  const [form] = Form.useForm();
  const watchedAgent = Form.useWatch("agent", form) ?? "";
  const watchedRole = Form.useWatch("role", form);
  const watchedUsername = Form.useWatch("username", form);
  const watchedActionType = Form.useWatch("action_type", form) as
    | string
    | null
    | undefined;

  const [autoCompleteKey, setAutoCompleteKey] = useState(0);
  const [isAgentDirty, setIsAgentDirty] = useState(false);
  const [appliedFilters, setAppliedFilters] =
    useState<PanelFilters>(INITIAL_PANEL_FILTERS);

  const isDirty =
    isAgentDirty ||
    (watchedRole ?? null) !== appliedFilters.role ||
    (watchedUsername ?? "") !== appliedFilters.username ||
    (watchedActionType ?? null) !== appliedFilters.action_type;

  const [selectedRole, setSelectedRole] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<
    "ascend" | "descend" | null
  >(null);

  const start_date = dates?.[0]?.utc().format("YYYY-MM-DD");
  const end_date = dates?.[1]?.utc().format("YYYY-MM-DD");
  const listView = agentPerformanceView ? "agents" : "events";

  const scopeFilters = suspensionsScopeFilters({
    agentPerformanceView,
    selectedRole,
    appliedFilters,
  });

  const tableFilters: SuspensionsAALTableFilters = {
    view: listView,
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    page: currentPage,
    size: pageSize,
    sort: toAalSort(suspensionsSortFieldForApi(sortField ?? ""), sortOrder),
    ...scopeFilters,
  };

  const {
    items: tableItemsRaw,
    total: totalResults,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
  } = useGetAALSuspensionsTable(tableFilters);

  const kpiFilters = {
    start_date: start_date ?? "",
    end_date: end_date ?? "",
  };
  const { data: suspensionsKpiData, isLoading: isSuspensionsKpiLoading } =
    useGetAALSuspensionsKPIs(kpiFilters);

  const { data: roles } = useGetRoles();
  const { data: staffData } = useGetStaff({
    page_size: 100,
  });

  const agentOptions = (staffData?.items ?? []).map((s) => ({
    value: s.email,
    label: s.email,
    id: s.id,
  }));

  const roleOptions = (roles ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  const roleById = Object.fromEntries(
    (roles ?? []).map((r) => [
      r.id,
      { name: r.name, color: r.color ?? "#6b7280" },
    ]),
  ) as Record<string, { name: string; color: string }>;

  useEffect(() => {
    if (agentPerformanceView) {
      setCurrentPage(1);
    }
  }, [selectedRole, agentPerformanceView]);

  useEffect(() => {
    if (!agentPerformanceView) {
      setSelectedRole([]);
    }
    setSortField(null);
    setSortOrder(null);
    setCurrentPage(1);
  }, [agentPerformanceView]);

  const { start: downloadTable } = useDownloadAALSuspensionsTable(listView);
  useDownloadOnTrigger(downloadTrigger, downloadTable, () => ({
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    ...scopeFilters,
  }));

  const tableItems = useMemo(
    (): SuspensionsRow[] => (tableItemsRaw ?? []) as SuspensionsRow[],
    [tableItemsRaw],
  );

  const apvDataSource = useMemo(
    (): ApvDataRow[] =>
      tableItems.map((row, index) => ({
        ...row,
        key: row.agent_id ?? row.event_id ?? String(index),
      })),
    [tableItems],
  );

  const usersSuspendedTotal = parseFloat(
    String(suspensionsKpiData?.users_suspended_delta ?? "0"),
  );
  const usersUnsuspendedTotal = parseFloat(
    String(suspensionsKpiData?.users_unsuspended_delta ?? "0"),
  );

  const handleClearFilters = () => {
    form.resetFields();
    form.setFieldsValue({
      agent: "",
      username: "",
      role: null,
      action_type: null,
    });
    setAutoCompleteKey((k) => k + 1);
    setIsAgentDirty(false);
    setAppliedFilters(INITIAL_PANEL_FILTERS);
    setCurrentPage(1);
  };

  const handleApply = () => {
    const values = form.getFieldsValue();
    setAppliedFilters({
      agent: form.getFieldValue("agent") ?? "",
      role: values.role ?? null,
      username: values.username ?? "",
      action_type: values.action_type ?? null,
    });
    setIsAgentDirty(false);
    setCurrentPage(1);
  };

  const handleTableChange: TableProps<SuspensionsRow>["onChange"] = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<SuspensionsRow> | SorterResult<SuspensionsRow>[],
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = sortFieldFromSorter(s?.field);
    if (field) {
      setSortField(field);
      setSortOrder(
        s.order === "ascend" || s.order === "descend" ? s.order : null,
      );
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const apvSuspendedValue = (record: SuspensionsRow) =>
    Number(record.users_suspended ?? record.users_suspended_delta ?? 0);
  const apvUnsuspendedValue = (record: SuspensionsRow) =>
    Number(record.users_unsuspended ?? record.users_unsuspended_delta ?? 0);
  const apvSuspendedPct = (record: SuspensionsRow) =>
    record.users_suspended_percent ?? record.users_suspended_pct_change;
  const apvUnsuspendedPct = (record: SuspensionsRow) =>
    record.users_unsuspended_percent ?? record.users_unsuspended_pct_change;

  const columns: TableProps<SuspensionsRow>["columns"] = [
    {
      title: "Date&Time",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      width: 250,
      render: (text: string) => <span>{convertToUserTimezone(text)}</span>,
    },
    {
      title: "Username",
      dataIndex: "target_username",
      key: "target_username",
      render: (text: string, record: SuspensionsRow) => (
        <SuspensionsExternalLink
          href={`${NAVIGATION_ROUTES.USER_MANAGER}?userId=${record.target_user_id}`}
        >
          {text}
        </SuspensionsExternalLink>
      ),
      width: 200,
    },
    {
      title: "PAM Agent",
      dataIndex: "agent_email",
      key: "agent_email",
      showSorterTooltip: false,
      render: (text: string, record: SuspensionsRow) => (
        <SuspensionsExternalLink
          href={getManualActionsStaffManagementHref(record.agent_id)}
        >
          {text}
        </SuspensionsExternalLink>
      ),
      width: 200,
    },
    {
      title: "Settlement Decision",
      dataIndex: "action_type",
      key: "action_type",
      render: (text: string, record: SuspensionsRow) => (
        <span
          className="suspensions-manual-actions-status-badge"
          style={{ backgroundColor: decisionColor(record.action_type ?? text) }}
        >
          {text}
        </span>
      ),
      width: 200,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (text: string) => <span>{text}</span>,
      width: 200,
    },
  ];

  const agentPerformanceColumns: TableProps<ApvDataRow>["columns"] = [
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent_email",
      key: "agent_email",
      width: 250,
      render: (text: string, record: ApvDataRow) => (
        <SuspensionsExternalLink
          href={getManualActionsStaffManagementHref(record.agent_id)}
        >
          {text}
        </SuspensionsExternalLink>
      ),
    },
    {
      title: AppStrings.USERS_SUSPENDED,
      dataIndex: "users_suspended",
      key: "users_suspended",
      width: 240,
      sorter: true,
      render: (_text: string, record: ApvDataRow) => (
        <AgentPerformanceMetricCell
          count={apvSuspendedValue(record)}
          percent={apvSuspendedPct(record)}
        />
      ),
    },
    {
      title: AppStrings.USERS_UNSUSPENDED,
      dataIndex: "users_unsuspended",
      key: "users_unsuspended",
      width: 240,
      sorter: true,
      render: (_text: string, record: ApvDataRow) => (
        <AgentPerformanceMetricCell
          count={apvUnsuspendedValue(record)}
          percent={apvUnsuspendedPct(record)}
        />
      ),
    },
    {
      title: AppStrings.ROLE,
      dataIndex: "agent_role_id",
      key: "agent_role_id",
      width: 170,
      render: (value: string) => (
        <RoleNameWithDot
          role={roleById[value]}
          fallbackLabel={value || "—"}
        />
      ),
    },
  ];

  const detailFiltersVisible = showFilters && !agentPerformanceView;
  const isTableBusy = isTableLoading || isTableFetching;
  const tablePagination = useMemo(
    () => ({
      current: currentPage,
      pageSize,
      total: totalResults,
      onChange: (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
      },
    }),
    [currentPage, pageSize, totalResults],
  );

  const tableSection = agentPerformanceView ? (
    <AgentPerformanceView
      columns={agentPerformanceColumns}
      handleTableChange={handleTableChange}
      filteredData={apvDataSource}
      roleOptions={roleOptions}
      selectedRoles={selectedRole}
      onRoleChange={setSelectedRole}
      pagination={tablePagination}
      isLoading={isTableBusy}
    />
  ) : (
    <TypedSuspensionsDataTable
      columns={columns}
      dataSource={tableItems}
      loading={isTableBusy}
      pagination={tablePagination}
      onChange={handleTableChange}
      rowKey={(record) => record.event_id}
      scroll={{ x: 1200, y: 500 }}
    />
  );

  return (
    <div className="suspensions-manual-actions-container">
      <Form form={form}>
        <div
          className="suspensions-manual-actions-selectors"
          style={flexRowDisplay(detailFiltersVisible)}
        >
          <div className="suspensions-manual-actions-selector">
            <span>{AppStrings.PAM_AGENT}</span>
            <TypedAgentAutoComplete
              key={autoCompleteKey}
              allOptions={agentOptions}
              selector="id"
              value={watchedAgent}
              handleChange={(id: string) => {
                form.setFieldValue("agent", id);
                setIsAgentDirty(true);
              }}
              clearOptions={() => form.setFieldValue("agent", "")}
              placeholder={AppStrings.NOT_SELECTED}
            />
          </div>
          <div className="suspensions-manual-actions-selector">
            <span>{AppStrings.USERNAME}</span>
            <Form.Item name="username" noStyle>
              <Input
                placeholder="Text"
                className="suspensions-manual-actions-filter-input"
              />
            </Form.Item>
          </div>
          <div className="suspensions-manual-actions-selector">
            <span>{AppStrings.ROLE}</span>
            <Form.Item name="role" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={roleOptions}
              />
            </Form.Item>
          </div>
          <div className="suspensions-manual-actions-selector">
            <span>Decision</span>
            <Form.Item name="action_type" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={decisionOptions}
              />
            </Form.Item>
          </div>
        </div>
      </Form>
      <div
        className="suspensions-manual-actions-selector-action-buttons"
        style={flexRowDisplay(detailFiltersVisible)}
      >
        <button type="button" className={isDirty ? "clear dirty" : "clear"} onClick={handleClearFilters}>
          {AppStrings.CLEAR_FILTERS}
        </button>
        <button
          type="button"
          className={isDirty ? "apply dirty" : "apply"}
          onClick={handleApply}
        >
          {AppStrings.APPLY}
        </button>
      </div>
      <div
        className="suspensions-manual-actions-kpi-cards"
        style={flexRowDisplay(!agentPerformanceView)}
      >
        <KpiCard
          title={AppStrings.USERS_SUSPENDED}
          value={usersSuspendedTotal}
          percentage={suspensionsKpiData?.users_suspended_pct_change}
          hugeValue={false}
          backgroundColor={KPI_CARD_BACKGROUND.DARK}
          isApiRunning={isSuspensionsKpiLoading}
          maximumFractionDigits={0}
        />
        <KpiCard
          title={AppStrings.USERS_UNSUSPENDED}
          value={usersUnsuspendedTotal}
          percentage={suspensionsKpiData?.users_unsuspended_pct_change}
          hugeValue={false}
          backgroundColor={KPI_CARD_BACKGROUND.DARK}
          isApiRunning={isSuspensionsKpiLoading}
          maximumFractionDigits={0}
        />
      </div>
      <div className="suspensions-manual-actions-table-container">
        {tableSection}
      </div>
    </div>
  );
}

export default SuspensionsManualActions;
